import { useEffect, useState } from 'react';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Button, ButtonType } from '../../../../components/Button';
import { ModalBase } from './types';

// -- Other Services Edit Modal --------------------------------------------
// Three rows (pet / senior / household) each with toggles on the left and a
// free-form "experience" textarea on the right. Household opens an
// additional set of specialServices sub-checkboxes when "Set service details"
// is clicked.
const OTHER_EXPERIENCE_MAX = 500;

const Toggle = ({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: string;
}): JSX.Element => (
  <label htmlFor={id} className='inline-flex items-center gap-2 cursor-pointer select-none'>
    <span className='relative inline-block w-10 h-5 align-middle'>
      <input
        id={id}
        type='checkbox'
        className='sr-only peer'
        checked={checked}
        onChange={(e): void => onChange(e.target.checked)}
      />
      <span className='absolute inset-0 bg-neutral-300 rounded-full peer-checked:bg-sapphire transition-colors' />
      <span className='absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full transition-transform peer-checked:translate-x-5' />
    </span>
    {label && <span className='font-medium'>{label}</span>}
  </label>
);

export const OtherServicesEditModal = ({
  isOpen,
  onClose,
  initialWillSitDog,
  initialWillSitCat,
  initialWillSitOtherPet,
  initialWillSitSenior,
  initialWillSitHome,
  initialPetSittingExperience,
  initialSeniorSittingExperience,
  initialHomeSittingExperience,
  initialSpecialServiceIds,
  specialServices,
  onSave,
}: ModalBase & {
  initialWillSitDog?: boolean;
  initialWillSitCat?: boolean;
  initialWillSitOtherPet?: boolean;
  initialWillSitSenior?: boolean;
  initialWillSitHome?: boolean;
  initialPetSittingExperience?: string;
  initialSeniorSittingExperience?: string;
  initialHomeSittingExperience?: string;
  initialSpecialServiceIds: number[];
  specialServices: Array<{ id: number; name: string }>;
  onSave: (patch: {
    willSitDog: boolean;
    willSitCat: boolean;
    willSitOtherPet: boolean;
    willSitSenior: boolean;
    willSitHome: boolean;
    petSittingExperience?: string;
    seniorSittingExperience?: string;
    homeSittingExperience?: string;
    specialServices: Array<{ id: number; name: string }>;
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [dog, setDog] = useState(!!initialWillSitDog);
  const [cat, setCat] = useState(!!initialWillSitCat);
  const [otherPet, setOtherPet] = useState(!!initialWillSitOtherPet);
  const [senior, setSenior] = useState(!!initialWillSitSenior);
  const [home, setHome] = useState(!!initialWillSitHome);
  const [petText, setPetText] = useState(initialPetSittingExperience ?? '');
  const [seniorText, setSeniorText] = useState(initialSeniorSittingExperience ?? '');
  const [homeText, setHomeText] = useState(initialHomeSittingExperience ?? '');
  const [serviceIds, setServiceIds] = useState<Set<number>>(
    new Set(initialSpecialServiceIds),
  );
  const [servicesVisible, setServicesVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDog(!!initialWillSitDog);
      setCat(!!initialWillSitCat);
      setOtherPet(!!initialWillSitOtherPet);
      setSenior(!!initialWillSitSenior);
      setHome(!!initialWillSitHome);
      setPetText(initialPetSittingExperience ?? '');
      setSeniorText(initialSeniorSittingExperience ?? '');
      setHomeText(initialHomeSittingExperience ?? '');
      setServiceIds(new Set(initialSpecialServiceIds));
      setServicesVisible(false);
    }
  }, [
    isOpen,
    initialWillSitDog,
    initialWillSitCat,
    initialWillSitOtherPet,
    initialWillSitSenior,
    initialWillSitHome,
    initialPetSittingExperience,
    initialSeniorSittingExperience,
    initialHomeSittingExperience,
    initialSpecialServiceIds,
  ]);

  const hasPetcare = dog || cat || otherPet;

  const toggleService = (id: number): void => {
    setServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      const selectedServices = specialServices.filter((s) => serviceIds.has(s.id));
      await onSave({
        willSitDog: dog,
        willSitCat: cat,
        willSitOtherPet: otherPet,
        willSitSenior: senior,
        willSitHome: home,
        petSittingExperience: petText || undefined,
        seniorSittingExperience: seniorText || undefined,
        homeSittingExperience: homeText || undefined,
        specialServices: selectedServices,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = (text: string): number => OTHER_EXPERIENCE_MAX - text.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editOtherServices')}
      className='w-full max-w-3xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-sitter-petcare'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mb-6'>
        <div className='sm:col-span-7'>
          <h3 className='font-semibold text-lg mb-2'>Pet Care</h3>
          <p className='mb-2'>
            Willing to provide pet care for any of the following&nbsp;animals?
          </p>
          <div className='space-y-2'>
            <Toggle id='will-sit-dog' checked={dog} onChange={setDog} label='Dogs' />
            <div />
            <Toggle id='will-sit-cat' checked={cat} onChange={setCat} label='Cats' />
            <div />
            <Toggle
              id='will-sit-other-pet'
              checked={otherPet}
              onChange={setOtherPet}
              label='Other pets'
            />
          </div>
        </div>
        <div className='sm:col-span-5'>
          <textarea
            aria-label={t('pages.sitterProfile.modals.petExperienceAriaLabel')}
            id='pet-sitting-experience'
            className='form-control w-full border border-neutral-300 rounded p-2 disabled:bg-neutral-100'
            rows={6}
            disabled={!hasPetcare}
            maxLength={OTHER_EXPERIENCE_MAX}
            value={petText}
            onChange={(e): void => setPetText(e.target.value)}
            placeholder={t('pages.sitterProfile.modals.petExperiencePlaceholder')}
          />
          <i className='text-xs text-neutral-500 block text-right mt-1 not-italic'>
            {remaining(petText)} characters remaining
          </i>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mb-6'>
        <div className='sm:col-span-7'>
          <h3 className='font-semibold text-lg mb-2'>Senior Care</h3>
          <p className='mb-2'>Willing to provide non-medical senior care?</p>
          <div className='flex items-center gap-3'>
            <Toggle id='will-sit-senior' checked={senior} onChange={setSenior} />
            <span>{senior ? 'Yes' : 'No'}</span>
          </div>
        </div>
        <div className='sm:col-span-5'>
          <textarea
            aria-label={t('pages.sitterProfile.modals.seniorExperienceAriaLabel')}
            id='senior-sitting-experience'
            className='form-control w-full border border-neutral-300 rounded p-2 disabled:bg-neutral-100'
            rows={6}
            disabled={!senior}
            maxLength={OTHER_EXPERIENCE_MAX}
            value={seniorText}
            onChange={(e): void => setSeniorText(e.target.value)}
            placeholder={t('pages.sitterProfile.modals.seniorExperiencePlaceholder')}
          />
          <i className='text-xs text-neutral-500 block text-right mt-1 not-italic'>
            {remaining(seniorText)} characters remaining
          </i>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4'>
        <div className='sm:col-span-7'>
          <h3 className='font-semibold text-lg mb-2'>Household Services</h3>
          <p className='mb-2'>
            Willing to provide household services that don&apos;t involve children,
            pets, or senior&nbsp;care?
          </p>
          <div className='flex items-center gap-3 flex-wrap'>
            <Toggle id='will-sit-home' checked={home} onChange={setHome} />
            <span>{home ? 'Yes' : 'No'}</span>
            {home && (
              <Button
                buttonType={ButtonType.secondary}
                onClick={(): void => setServicesVisible((v) => !v)}
              >
                {servicesVisible ? 'Hide' : 'Set'} service details
              </Button>
            )}
          </div>
          {home && servicesVisible && (
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3'>
              {specialServices.map((service) => (
                <label
                  key={service.id}
                  className='flex items-start gap-2 cursor-pointer'
                >
                  <input
                    id={`edit-service-${service.id}`}
                    type='checkbox'
                    className='mt-1'
                    checked={serviceIds.has(service.id)}
                    onChange={(): void => toggleService(service.id)}
                  />
                  <span>{service.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className='sm:col-span-5'>
          <textarea
            aria-label={t('pages.sitterProfile.modals.householdExperienceAriaLabel')}
            id='home-sitting-experience'
            className='form-control w-full border border-neutral-300 rounded p-2 disabled:bg-neutral-100'
            rows={6}
            disabled={!home}
            maxLength={OTHER_EXPERIENCE_MAX}
            value={homeText}
            onChange={(e): void => setHomeText(e.target.value)}
            placeholder={t('pages.sitterProfile.modals.householdExperiencePlaceholder')}
          />
          <i className='text-xs text-neutral-500 block text-right mt-1 not-italic'>
            {remaining(homeText)} characters remaining
          </i>
        </div>
      </div>

    </Modal>
  );
};
